/* Native touch scrolling, small curved transforms, no carousel dependency. */
(() => {
  if (customElements.get('mt-inspiration')) return;
  class MTInspiration extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.viewport = this.querySelector('.mt-inspiration__viewport');
      this.track = this.querySelector('.mt-inspiration__track');
      this.originals = Array.from(this.track?.children || []);
      if (!this.viewport || this.originals.length < 2) return;
      this.controller = new AbortController();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.frame = 0;
      this.paintFrame = 0;
      this.visible = false;
      this.interacted = false;
      this.hovering = false;
      this.focused = false;
      this.drag = null;
      this.rebuild();
      const on = (target, type, fn, options = {}) => target.addEventListener(type, fn, { ...options, signal: this.controller.signal });
      on(this.viewport, 'scroll', () => { if (!this.frame) this.queuePaint(); }, { passive: true });
      on(this.viewport, 'wheel', () => this.takeControl(), { passive: true });
      on(this.viewport, 'keydown', event => {
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown', ' '].includes(event.key)) this.takeControl();
      });
      on(this.viewport, 'pointerenter', event => {
        if (event.pointerType === 'mouse') { this.hovering = true; this.sync(); }
      });
      on(this.viewport, 'pointerleave', event => {
        if (event.pointerType === 'mouse') { this.hovering = false; this.sync(); }
      });
      on(this.viewport, 'pointerdown', event => {
        if (!event.isPrimary || event.button !== 0) return;
        this.takeControl();
        this.suppressClick = false;
        if (event.pointerType !== 'mouse') return;
        this.drag = { x: event.clientX, scroll: this.viewport.scrollLeft, id: event.pointerId, moved: false };
      });
      on(this.viewport, 'pointermove', event => {
        if (!this.drag || this.drag.id !== event.pointerId) return;
        const distance = event.clientX - this.drag.x;
        if (!this.drag.moved && Math.abs(distance) > 6) {
          this.drag.moved = true;
          this.classList.add('is-dragging');
          this.viewport.setPointerCapture(event.pointerId);
        }
        if (this.drag.moved) {
          event.preventDefault();
          this.viewport.scrollLeft = this.drag.scroll - distance;
        }
      });
      const endDrag = event => {
        if (!this.drag || this.drag.id !== event.pointerId) return;
        this.suppressClick = this.drag.moved;
        if (this.viewport.hasPointerCapture(event.pointerId)) this.viewport.releasePointerCapture(event.pointerId);
        this.drag = null;
        this.classList.remove('is-dragging');
        this.queuePaint();
      };
      on(window, 'pointerup', endDrag);
      on(window, 'pointercancel', endDrag);
      on(this.viewport, 'click', event => {
        if (this.suppressClick) { event.preventDefault(); event.stopPropagation(); this.suppressClick = false; }
      }, { capture: true });
      on(this.viewport, 'dragstart', event => event.preventDefault());
      on(this, 'focusin', () => { this.focused = true; this.sync(); });
      on(this, 'focusout', event => {
        this.focused = this.contains(event.relatedTarget);
        this.sync();
      });
      on(document, 'visibilitychange', () => this.sync());
      on(this.motion, 'change', () => { this.rebuild(); this.sync(); });
      on(this, 'shopify:block:select', event => {
        this.takeControl();
        const item = event.target.closest('.mt-inspiration__item');
        if (item) this.viewport.scrollLeft = item.offsetLeft - (this.width - this.cardWidth) / 2;
      });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => { this.measure(); this.sync(); });
        this.resizeObserver.observe(this.viewport);
      } else on(window, 'resize', () => this.measure(), { passive: true });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting;
          this.sync();
        }, { threshold: 0 });
        this.observer.observe(this);
      } else { this.visible = true; this.sync(); }
    }

    rebuild() {
      this.track.querySelectorAll('[data-mt-clone]').forEach(item => item.remove());
      this.loop = !this.motion.matches && this.originals.length >= 6;
      if (this.loop) {
        const clone = item => {
          const copy = item.cloneNode(true);
          copy.dataset.mtClone = '';
          copy.setAttribute('aria-hidden', 'true');
          [copy, ...copy.querySelectorAll('*')].forEach(node => {
            node.removeAttribute('id');
            node.removeAttribute('data-shopify-editor-block');
            if (node.matches('a, button, [tabindex]')) node.setAttribute('tabindex', '-1');
          });
          return copy;
        };
        this.track.prepend(...this.originals.map(clone));
        this.track.append(...this.originals.map(clone));
      }
      this.items = Array.from(this.track.children);
      this.cards = this.items.map(item => item.querySelector('.mt-inspiration__card'));
      this.classList.add('is-enhanced');
      this.measure(true);
    }

    measure(reset = false) {
      const previousStride = this.stride;
      const relative = previousStride ? (this.viewport.scrollLeft + this.width / 2) / previousStride : 0;
      this.width = this.viewport.clientWidth;
      this.cardWidth = this.originals[0].offsetWidth;
      this.stride = this.cardWidth + (parseFloat(getComputedStyle(this.track).columnGap) || 0);
      this.cycle = this.stride * this.originals.length;
      if (!this.width || !this.stride) return;
      if (reset) this.viewport.scrollLeft = this.loop ? this.cycle - (this.width - this.cardWidth) / 2 : 0;
      else if (previousStride) this.viewport.scrollLeft = relative * this.stride - this.width / 2;
      this.position = this.viewport.scrollLeft;
      this.paint();
    }

    paint() {
      if (!this.width || !this.stride) return;
      const scroll = this.viewport.scrollLeft;
      const radius = Math.max(this.width * 1.7, 900);
      this.cards.forEach((card, index) => {
        const distance = index * this.stride + this.cardWidth / 2 - scroll - this.width / 2;
        if (Math.abs(distance) > this.width / 2 + this.cardWidth * 1.6) return;
        const y = this.motion.matches ? 0 : Math.min(this.width < 750 ? 30 : 112, distance * distance / (2 * radius));
        const angle = this.motion.matches ? 0 : Math.max(-20, Math.min(20, Math.atan(distance / radius) * 180 / Math.PI));
        card.style.setProperty('--mt-look-y', y.toFixed(2) + 'px');
        card.style.setProperty('--mt-look-angle', angle.toFixed(2) + 'deg');
      });
    }

    queuePaint() {
      if (this.paintFrame) return;
      this.paintFrame = requestAnimationFrame(() => { this.paintFrame = 0; this.paint(); });
    }

    takeControl() {
      this.interacted = true;
      this.sync();
    }

    sync() {
      const canRun = this.dataset.autoplay === 'true' && this.loop && this.width < this.cycle && this.visible && !document.hidden && !this.motion.matches && !navigator.connection?.saveData && !this.interacted && !this.focused && !this.hovering && !window.Shopify?.designMode;
      if (!canRun) {
        cancelAnimationFrame(this.frame);
        this.frame = 0;
        this.lastTime = 0;
        return;
      }
      if (this.frame) return;
      this.position = this.viewport.scrollLeft;
      this.lastTime = 0;
      const tick = time => {
        const delta = this.lastTime ? Math.min(time - this.lastTime, 50) : 0;
        this.lastTime = time;
        this.position += delta * (this.width < 750 ? .018 : .026);
        if (this.position >= this.cycle * 1.5) this.position -= this.cycle;
        this.viewport.scrollLeft = this.position;
        this.paint();
        this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.paintFrame);
      this.track?.querySelectorAll('[data-mt-clone]').forEach(item => item.remove());
      this.classList.remove('is-enhanced', 'is-dragging');
    }
  }
  customElements.define('mt-inspiration', MTInspiration);
})();
